import React from 'react';
import {Box} from 'src/design/atoms/Box';
import {useDisclosure} from '@mantine/hooks';
import {useSearchParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkTrainingSignOffs} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {TrainingLandingTabs} from 'src/pages/Modules/Training/shared/TrainingLandingTabs';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {SignOffTabView} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {NewSignOffModal} from './components/NewSignOffModal';
import {SignOffInboxTab} from './components/SignOffInboxTab';
import {SignOffOversightTab} from './components/SignOffOversightTab';
import {SignOffRecordsTab} from './components/SignOffRecordsTab';
import {SkillMatrixTab} from './components/SkillMatrixTab';
import {SkillsTab} from './components/SkillsTab';

/**
 * Sign-offs — top-level Training destination. Replaces the
 * throwaway-TaskDefinition demonstration mechanism with first-class
 * Skill/SkillSignOff records. My Queue is the evaluator inbox; Oversight
 * gives admins visibility into every pending request instance-wide; Skill
 * Matrix is the auditor artifact; Skills is CRUD for the skill/criteria
 * catalog; Records is the full evaluated history (including "Not yet
 * competent" outcomes, which otherwise fall off My Queue/Oversight once
 * resolved). Active tab lives in the URL (`?tab=`) so links (e.g. the
 * closeout dialog's pending-demonstrations count, or returning from
 * Evaluate) land on the right tab; an optional `?courseName=` seeds a
 * client-side Course column filter on Oversight for the same reason.
 */
const SignOffsPage: React.FC = () => {
  useSetDocumentTitle(TrainingDocumentTitle);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as SignOffTabView) || 'my-queue';
  const initialOversightCourseName =
    searchParams.get('courseName') ?? undefined;

  const [newSignOffOpened, newSignOffHandlers] = useDisclosure(false);
  const {hasPermission: canManage} = useHasModulePermission({
    module: ModuleEnum.Training,
    operation: PermissionOperationEnum.Update,
  });

  const {RenderedTabsComponent} = useUrlQueryTabV2<SignOffTabView>({
    view: activeTab,
    defaultView: 'my-queue',
    tabs: [
      {
        view: 'my-queue',
        label: 'My Queue',
        component: (
          <SectionCard title="My Queue">
            <Box px="sm" py="xs">
              <SignOffInboxTab />
            </Box>
          </SectionCard>
        ),
      },
      {
        // MRT tables render their own Paper surface (toolbar, pagination,
        // reset footer) — no SectionCard wrapper here, or the result is a
        // card-in-card double surface.
        view: 'oversight',
        label: 'Oversight',
        component: (
          <SignOffOversightTab initialCourseName={initialOversightCourseName} />
        ),
      },
      {
        view: 'matrix',
        label: 'Skill Matrix',
        component: (
          <SectionCard title="Skill Matrix">
            <Box px="sm" py="xs">
              <SkillMatrixTab />
            </Box>
          </SectionCard>
        ),
      },
      {
        // Same MRT-surface reasoning as Oversight above.
        view: 'records',
        label: 'Records',
        component: <SignOffRecordsTab />,
      },
      {
        // Same MRT-surface reasoning as Oversight above.
        view: 'skills',
        label: 'Skills',
        component: <SkillsTab canManage={canManage} />,
      },
    ],
    onSelectView: nextView => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set('tab', nextView);
          next.delete('courseName');
          return next;
        },
        {replace: true}
      );
    },
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkTrainingSignOffs.icon}>
          Sign-offs
        </PageHeader.Title>
        {canManage && (
          <PageHeader.Actions>
            <Button onClick={newSignOffHandlers.open}>New sign-off</Button>
          </PageHeader.Actions>
        )}
      </PageHeader>

      <TrainingLandingTabs active="sign-offs" />

      {RenderedTabsComponent}

      <NewSignOffModal
        opened={newSignOffOpened}
        onClose={newSignOffHandlers.close}
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingSignOffs(),
  element: <SignOffsPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default SignOffsPage;
